import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import createNotification from '../../../utils/createNotification';
import {
    ConsiderationField,
    ConsiderationFieldChange,
    ConsiderationLedger as ConsiderationLedgerModel,
    ConsiderationSection,
    ConsiderationTermBase,
    FlowOfFunds,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { GetFlowOfFunds } from '../../../slices/marketplace/flow-of-funds/thunks/get-flow-of-funds.thunk';
import {
    SaveConsiderationRow,
    SaveConsiderationRowRejection,
} from '../../../slices/marketplace/flow-of-funds/thunks/save-consideration-row.thunk';
import { SaveConsiderationComment } from '../../../slices/marketplace/flow-of-funds/thunks/save-consideration-comment.thunk';
import { ConsiderationAutoSaveReporter } from './ConsiderationAutoSaveContext';

const FLUSH_DEBOUNCE_MS = 2000;
const CONFLICT_MESSAGE = 'Someone else changed this row. The latest values have been loaded.';

export interface ConsiderationAutosave {
    isRowDirty: (section: ConsiderationSection, rowKey: number) => boolean;
    pendingFieldValue: (
        section: ConsiderationSection,
        rowKey: number,
        field: ConsiderationField
    ) => string | null | undefined;
    setFieldValue: (
        section: ConsiderationSection,
        rowKey: number,
        field: ConsiderationField,
        nextWireValue: string | null
    ) => void;
    pendingComment: (section: ConsiderationSection, rowKey: number) => string | null | undefined;
    setComment: (section: ConsiderationSection, rowKey: number, nextValue: string | null) => void;
    flushRowForRemoval: (section: ConsiderationSection, rowKey: number) => Promise<string | null>;
    flush: () => void;
}

interface PendingDetail<T> {
    touched: boolean;
    value: T;
}

const NOT_TOUCHED: PendingDetail<null> = { touched: false, value: null };

interface DirtyConsiderationRow {
    section: ConsiderationSection;
    rowKey: number;
    values: Map<ConsiderationField, string | null>;
    comment: PendingDetail<string | null>;
}

interface RowIndexEntry {
    version: string | null;
    fieldChanges: ConsiderationFieldChange[];
}

const rowIndexKey = (section: ConsiderationSection, rowKey: number) => `${section}:${rowKey}`;

const indexTerm = (
    index: Map<string, RowIndexEntry>,
    section: ConsiderationSection,
    term: ConsiderationTermBase | null | undefined
) => {
    if (!term) {
        return;
    }
    index.set(rowIndexKey(section, term.id), {
        version: term.version,
        fieldChanges: term.fieldChanges,
    });
};

const buildRowIndex = (consideration: ConsiderationLedgerModel): Map<string, RowIndexEntry> => {
    const index = new Map<string, RowIndexEntry>();
    consideration.rows.forEach((row) => {
        index.set(rowIndexKey(ConsiderationSection.Summary, row.considerationType), {
            version: row.version,
            fieldChanges: row.fieldChanges,
        });
    });
    consideration.detail.deferredFixedPayments.forEach((term) =>
        indexTerm(index, ConsiderationSection.DeferredFixedPayments, term)
    );
    consideration.detail.deferredVariablePayments.forEach((term) =>
        indexTerm(index, ConsiderationSection.DeferredVariablePayments, term)
    );
    indexTerm(index, ConsiderationSection.JvEquity, consideration.detail.jvEquity);
    indexTerm(index, ConsiderationSection.CommonEquity, consideration.detail.commonCorporateEquity);
    indexTerm(
        index,
        ConsiderationSection.PreferredEquity,
        consideration.detail.preferredCorporateEquity
    );
    return index;
};

export const useConsiderationAutosave = (
    consideration: ConsiderationLedgerModel,
    engagementId: number,
    reporter: ConsiderationAutoSaveReporter
): ConsiderationAutosave => {
    const dispatch = useDispatch() as any;

    const rowIndexRef = useRef(buildRowIndex(consideration));
    rowIndexRef.current = buildRowIndex(consideration);
    const reporterRef = useRef(reporter);
    reporterRef.current = reporter;

    const dirtyRowsRef = useRef(new Map<string, DirtyConsiderationRow>());
    const isSavingRef = useRef(false);
    const hasFollowUpFlushRef = useRef(false);
    const activeFlushPromiseRef = useRef<Promise<void> | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);

    const [, setDirtyVersion] = useState(0);

    const bumpDirtyVersion = () => {
        if (isMountedRef.current) {
            setDirtyVersion((version) => version + 1);
        }
    };

    const clearDebounce = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    const adoptServerState = (flowOfFunds: FlowOfFunds) => {
        rowIndexRef.current = buildRowIndex(flowOfFunds.consideration);
    };

    const latestVersion = (section: ConsiderationSection, rowKey: number) =>
        rowIndexRef.current.get(rowIndexKey(section, rowKey))?.version ?? null;

    const latestFieldValue = (
        section: ConsiderationSection,
        rowKey: number,
        field: ConsiderationField
    ): string | null | undefined =>
        rowIndexRef.current
            .get(rowIndexKey(section, rowKey))
            ?.fieldChanges.find((change) => change.field === field)?.currentValue;

    const getOrCreateDirtyRow = (
        key: string,
        section: ConsiderationSection,
        rowKey: number
    ): DirtyConsiderationRow => {
        let dirty = dirtyRowsRef.current.get(key);
        if (!dirty) {
            dirty = { section, rowKey, values: new Map(), comment: NOT_TOUCHED };
            dirtyRowsRef.current.set(key, dirty);
        }
        return dirty;
    };

    const pruneCleanRow = (key: string, dirty: DirtyConsiderationRow) => {
        if (dirty.values.size === 0 && !dirty.comment.touched) {
            dirtyRowsRef.current.delete(key);
        }
    };

    const applyRowEdit = (
        section: ConsiderationSection,
        rowKey: number,
        assign: (dirty: DirtyConsiderationRow) => void
    ) => {
        const key = rowIndexKey(section, rowKey);
        const dirty = getOrCreateDirtyRow(key, section, rowKey);
        assign(dirty);
        pruneCleanRow(key, dirty);
        bumpDirtyVersion();
        scheduleDebounce();
    };

    const setFieldValue = (
        section: ConsiderationSection,
        rowKey: number,
        field: ConsiderationField,
        nextWireValue: string | null
    ) =>
        applyRowEdit(section, rowKey, (dirty) => {
            const serverValue = latestFieldValue(section, rowKey, field) ?? null;
            if (nextWireValue === serverValue) {
                dirty.values.delete(field);
            } else {
                dirty.values.set(field, nextWireValue);
            }
        });

    const setComment = (section: ConsiderationSection, rowKey: number, nextValue: string | null) =>
        applyRowEdit(section, rowKey, (dirty) => {
            dirty.comment = { touched: true, value: nextValue };
        });

    const saveRowValues = async (
        section: ConsiderationSection,
        rowKey: number,
        values: Map<ConsiderationField, string | null>
    ): Promise<'ok' | 'conflict' | 'error'> => {
        reporterRef.current.reportStart();
        const action = await dispatch(
            SaveConsiderationRow({
                engagementId,
                section,
                rowKey,
                version: latestVersion(section, rowKey),
                values: Array.from(values.entries()).map(([field, value]) => ({ field, value })),
            })
        );
        if (SaveConsiderationRow.fulfilled.match(action)) {
            adoptServerState(action.payload);
            reporterRef.current.reportOutcome('ok');
            return 'ok';
        }
        const rejection = action.payload as SaveConsiderationRowRejection | undefined;
        if (rejection?.isConflict) {
            createNotification(CONFLICT_MESSAGE, 'error');
            dispatch(GetFlowOfFunds(engagementId));
            reporterRef.current.reportOutcome('conflict');
            return 'conflict';
        }
        reporterRef.current.reportOutcome('error');
        return 'error';
    };

    const saveRowComment = async (
        section: ConsiderationSection,
        rowKey: number,
        comment: string | null
    ): Promise<'ok' | 'error'> => {
        reporterRef.current.reportStart();
        const action = await dispatch(
            SaveConsiderationComment({ engagementId, section, rowKey, comment })
        );
        const isFulfilled = SaveConsiderationComment.fulfilled.match(action);
        if (isFulfilled) {
            adoptServerState(action.payload);
        }
        const outcome = isFulfilled ? 'ok' : 'error';
        reporterRef.current.reportOutcome(outcome);
        return outcome;
    };

    const clearSentValues = (key: string, sentValues: Map<ConsiderationField, string | null>) => {
        const dirty = dirtyRowsRef.current.get(key);
        if (!dirty) {
            return;
        }
        sentValues.forEach((sentValue, field) => {
            const stillPending = dirty.values.get(field);
            if (dirty.values.has(field) && stillPending === sentValue) {
                dirty.values.delete(field);
            }
        });
        pruneCleanRow(key, dirty);
    };

    const clearSentComment = (key: string, sentComment: PendingDetail<string | null>) => {
        const dirty = dirtyRowsRef.current.get(key);
        if (!dirty) {
            return;
        }
        if (dirty.comment === sentComment) {
            dirty.comment = NOT_TOUCHED;
        }
        pruneCleanRow(key, dirty);
    };

    const saveDirtyRow = async (key: string) => {
        let dirty = dirtyRowsRef.current.get(key);
        if (!dirty) {
            return;
        }
        const { section, rowKey } = dirty;

        if (dirty.values.size > 0) {
            const sentValues = new Map(dirty.values);
            const outcome = await saveRowValues(section, rowKey, sentValues);
            if (outcome === 'conflict') {
                dirtyRowsRef.current.delete(key);
                return;
            }
            if (outcome === 'ok') {
                clearSentValues(key, sentValues);
            }
        }

        dirty = dirtyRowsRef.current.get(key);
        if (dirty && dirty.comment.touched) {
            const sentComment = dirty.comment;
            const outcome = await saveRowComment(section, rowKey, sentComment.value);
            if (outcome === 'ok') {
                clearSentComment(key, sentComment);
            }
        }
    };

    const runFlush = (): Promise<void> => {
        clearDebounce();
        if (isSavingRef.current) {
            hasFollowUpFlushRef.current = true;
            return activeFlushPromiseRef.current ?? Promise.resolve();
        }
        if (dirtyRowsRef.current.size === 0) {
            return Promise.resolve();
        }

        isSavingRef.current = true;
        const promise = (async () => {
            const dirtyKeys = Array.from(dirtyRowsRef.current.keys());
            for (const key of dirtyKeys) {
                await saveDirtyRow(key);
            }
            isSavingRef.current = false;
            activeFlushPromiseRef.current = null;
            bumpDirtyVersion();
            if (hasFollowUpFlushRef.current) {
                hasFollowUpFlushRef.current = false;
                await runFlush();
            }
        })();
        activeFlushPromiseRef.current = promise;
        return promise;
    };

    const runFlushRef = useRef(runFlush);
    runFlushRef.current = runFlush;

    const scheduleDebounce = () => {
        clearDebounce();
        debounceTimerRef.current = setTimeout(() => runFlushRef.current(), FLUSH_DEBOUNCE_MS);
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimeout(debounceTimerRef.current ?? undefined);
            runFlushRef.current();
        };
    }, []);

    const flushRowForRemoval = async (
        section: ConsiderationSection,
        rowKey: number
    ): Promise<string | null> => {
        const key = rowIndexKey(section, rowKey);
        while (dirtyRowsRef.current.has(key) || isSavingRef.current) {
            await runFlushRef.current();
        }
        return latestVersion(section, rowKey);
    };

    return {
        isRowDirty: (section, rowKey) => dirtyRowsRef.current.has(rowIndexKey(section, rowKey)),
        pendingFieldValue: (section, rowKey, field) => {
            const dirty = dirtyRowsRef.current.get(rowIndexKey(section, rowKey));
            return dirty?.values.has(field) ? dirty.values.get(field) : undefined;
        },
        setFieldValue,
        pendingComment: (section, rowKey) => {
            const dirty = dirtyRowsRef.current.get(rowIndexKey(section, rowKey));
            return dirty?.comment.touched ? dirty.comment.value : undefined;
        },
        setComment,
        flushRowForRemoval,
        flush: () => {
            void runFlushRef.current();
        },
    };
};
